import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StudentExerciseCard from "@/components/exercises/StudentExerciseCard";
import { CalendarClock, Infinity as NoDue, RotateCcw, AlarmClockOff, CheckCircle2, ListChecks } from "lucide-react";

const dueTs = (d) => new Date(d.includes("T") ? d : `${d}T23:59`).getTime();
const open = (e) => !e.my_submission || e.my_submission.status === "da_rivedere";
const BUCKETS = {
  redo: { label: "Da rivedere", hint: "Il docente ha chiesto delle modifiche", icon: RotateCcw, tone: "text-amber-700",
    test: (e) => e.my_submission?.status === "da_rivedere" },
  due: { label: "Con scadenza", hint: "In ordine di scadenza, dalla più vicina", icon: CalendarClock, tone: "text-[#C2410C]",
    test: (e) => !e.my_submission && e.due_date && !e.is_late_now, sort: (a, b) => dueTs(a.due_date) - dueTs(b.due_date) },
  nodue: { label: "Senza scadenza", hint: "Consegnali quando vuoi", icon: NoDue, tone: "text-[#1C352D]",
    test: (e) => !e.my_submission && !e.due_date },
  late: { label: "Scaduti, non consegnati", hint: "Puoi ancora consegnarli: saranno segnati in ritardo", icon: AlarmClockOff, tone: "text-red-700",
    test: (e) => !e.my_submission && e.due_date && e.is_late_now, sort: (a, b) => dueTs(b.due_date) - dueTs(a.due_date) },
  done: { label: "Consegnati", hint: "In attesa di valutazione o già valutati", icon: CheckCircle2, tone: "text-emerald-700",
    test: (e) => e.my_submission && e.my_submission.status !== "da_rivedere",
    sort: (a, b) => (b.my_submission.submitted_at || "").localeCompare(a.my_submission.submitted_at || "") },
};
const TABS = [["todo", "Da consegnare", ["redo", "due", "nodue"]], ["late", "Scaduti", ["late"]], ["done", "Consegnati", ["done"]], ["all", "Tutti", ["redo", "due", "nodue", "late", "done"]]];

function Group({ k, items }) {
  const b = BUCKETS[k];
  const Icon = b.icon;
  if (!items.length) return null;
  return (
    <section className="space-y-3" data-testid={`exercises-group-${k}`}>
      <div className="flex items-baseline gap-2 pt-2">
        <Icon size={15} className={b.tone}/>
        <h2 className={`text-base font-semibold ${b.tone}`}>{b.label} <span className="font-mono text-sm">({items.length})</span></h2>
        <span className="text-xs text-stone-500 hidden sm:inline">· {b.hint}</span>
      </div>
      {items.map((e) => <StudentExerciseCard key={e.id} ex={e} queryKey={["my-exercises"]} showCourse/>)}
    </section>
  );
}

export default function PortalEsercizi() {
  const [tab, setTab] = useState("todo");
  const [course, setCourse] = useState("");
  const { data = [], isLoading } = useQuery({ queryKey: ["my-exercises"], queryFn: async () => (await api.get("/exercises/me")).data });
  const courses = useMemo(() => [...new Map(data.map((e) => [e.course_id, e.course_name])).entries()], [data]);
  const groups = useMemo(() => {
    const list = data.filter((e) => !course || e.course_id === course);
    return Object.fromEntries(Object.entries(BUCKETS).map(([k, b]) => {
      const items = list.filter(b.test);
      return [k, b.sort ? items.sort(b.sort) : items];
    }));
  }, [data, course]);
  const count = (keys) => keys.reduce((s, k) => s + groups[k].length, 0);
  const keys = TABS.find((t) => t[0] === tab)[2];

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1000px] mx-auto">
      <PageHeader title="Esercizi" subtitle="Esercizi dei tuoi corsi e seminari: scarica le consegne e invia i tuoi elaborati."/>
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {TABS.map(([v, l, ks]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`exercises-tab-${v}`}
            className={`px-3.5 py-1.5 rounded-full text-sm border transition-colors ${tab === v ? "bg-[#1C352D] text-white border-[#1C352D]" : "bg-white border-stone-200 text-stone-600 hover:border-stone-400"}`}>
            {l} <span className="font-mono text-xs opacity-80">{count(ks)}</span>
          </button>
        ))}
        {courses.length > 1 && (
          <select value={course} onChange={(e) => setCourse(e.target.value)} data-testid="exercises-course-filter"
            className="ml-auto h-9 rounded-full border border-stone-200 bg-white px-3 text-sm">
            <option value="">Tutti i corsi</option>
            {courses.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        )}
      </div>
      {isLoading && <div className="text-sm text-stone-400">Caricamento…</div>}
      {!isLoading && count(keys) === 0 && (
        <div className="p-10 text-center text-sm text-stone-400 border border-dashed border-stone-200 rounded-xl flex flex-col items-center gap-2" data-testid="exercises-empty">
          <ListChecks size={20}/>{tab === "todo" ? "Nessun esercizio da consegnare. Ottimo lavoro!" : "Nessun esercizio."}
        </div>
      )}
      <div className="space-y-6">{keys.map((k) => <Group key={k} k={k} items={groups[k]}/>)}</div>
    </div>
  );
}
